import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Save, Gauge, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { useFontSize } from './FontSizeContext';

const EnterMeterReadings = ({ isOpen, onClose, assetNum, meters, onRefresh }) => {
    const { t } = useTranslation(); // 🎯 تفعيل الـ Hook
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;
    const [readings, setReadings] = useState({});
    
    // جلب اسم المستخدم الحالي - 'username' مكنش بيتسجل في localStorage
    // خالص، فكانت بترجع 'UNKNOWN' دايماً بدل الاسم الحقيقي
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}').username || 'UNKNOWN';

    // منطق التحقق من وجود عدادات عند محاولة فتح المودال
    useEffect(() => {
        if (isOpen && (!meters || meters.length === 0)) {
            toast.error(`${t('enterMeters.noMeters')} ${assetNum}`, { // 🎯
                icon: '⚠️',
                duration: 4000
            });
            onClose(); 
        }
    }, [isOpen, meters, assetNum, onClose, t]);

    if (!isOpen || !meters || meters.length === 0) return null;

    const handleSave = async () => {
        const loadId = toast.loading(t('enterMeters.saving')); // 🎯
        try {
            const token = localStorage.getItem('token');
            const promises = Object.keys(readings).map(meterNum => {
                // التحقق أن المستخدم أدخل قيمة فعلاً لهذا العداد
                if(!readings[meterNum]?.value) return null; 
                
                return axios.post('/api/meter-readings/save', {
                    asset_num: assetNum,
                    meter_num: meterNum,
                    reading_value: parseFloat(readings[meterNum].value),
                    reading_date: readings[meterNum].date || new Date().toISOString()
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }).filter(p => p !== null);

            if (promises.length === 0) {
                toast.error(t('enterMeters.enterAtLeastOne'), { id: loadId }); // 🎯
                return;
            }

            await Promise.all(promises);
            toast.success(t('enterMeters.saveSuccess'), { id: loadId }); // 🎯
            if (onRefresh) onRefresh(); 
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || t('enterMeters.saveFailed'), { id: loadId }); // 🎯
        }
    };

    return (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-4xl shadow-2xl rounded-sm flex flex-col max-h-[90vh] border border-gray-300" style={{ fontSize: `${fontSize}px` }}>
                {/* Header - IBM Maximo Identity */}
                <div className="bg-[#005495] text-white p-3 flex justify-between items-center font-bold text-[12px] uppercase tracking-wider shadow-md">
                    <div className="flex items-center gap-2">
                        <Gauge size={18} className="text-blue-200" /> 
                        <span>{t('enterMeters.title')} <span className="text-orange-400">{assetNum}</span></span>
                    </div>
                    <button onClick={onClose} className="hover:bg-red-600 p-1 rounded transition-all">
                        <X size={20} />
                    </button>
                </div>

                {/* Body - Table Section */}
                <div className="p-6 overflow-y-auto bg-gray-50 flex-1">
                    <div className="bg-white border shadow-sm">
                        <table className="w-full text-[11px] border-collapse">
                            <thead>
                                <tr className="bg-gray-100 border-b-2 border-gray-200 text-[#005495] uppercase font-bold">
                                    <th className="p-3 text-left border-r w-1/4">{t('enterMeters.thIdentification')}</th>
                                    <th className="p-3 text-left border-r text-orange-700 w-1/4">
                                        <div className="flex items-center gap-2">{t('enterMeters.thNewReading')}</div>
                                    </th>
                                    <th className="p-3 text-left border-r text-orange-700 w-1/4">
                                        <div className="flex items-center gap-2">{t('enterMeters.thReadingDate')}</div>
                                    </th>
                                    <th className="p-3 text-left border-r">{t('enterMeters.thMeterType')}</th>
                                    <th className="p-3 text-left">{t('enterMeters.thLastRecorded')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {meters.map((m, idx) => (
                                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                                        <td className="p-3 border-r">
                                            <div className="font-bold text-gray-800">{m.meter_num}</div>
                                            <div className="text-[10px] text-gray-400 italic">{m.meter_name || t('enterMeters.noDescription')}</div>
                                        </td>
                                        <td className="p-3 border-r bg-orange-50/10 focus-within:bg-orange-50/30 transition-all">
                                            <input 
                                                type="number"
                                                placeholder={t('enterMeters.enterValue')}
                                                className="w-full bg-transparent outline-none border-b border-orange-200 focus:border-orange-600 font-black text-[#005495] text-lg text-center"
                                                onChange={(e) => setReadings({
                                                    ...readings, 
                                                    [m.meter_num]: { ...readings[m.meter_num], value: e.target.value }
                                                })}
                                            />
                                        </td>
                                        <td className="p-3 border-r">
                                            <input 
                                                type="datetime-local"
                                                className="w-full bg-transparent outline-none text-gray-600 font-mono"
                                                defaultValue={new Date().toISOString().slice(0, 16)}
                                                onChange={(e) => setReadings({
                                                    ...readings, 
                                                    [m.meter_num]: { ...readings[m.meter_num], date: e.target.value }
                                                })}
                                            />
                                        </td>
                                        <td className="p-3 border-r">
                                            <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-500 font-bold">{m.meter_type}</span>
                                        </td>
                                        <td className="p-3 font-mono text-right">
                                            <div className="text-gray-400">{m.last_reading || '0.00'}</div>
                                            <div className="text-[9px] text-gray-300">{m.last_reading_date || t('enterMeters.notAvailable')}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Section */}
                <div className="bg-gray-100 p-4 border-t flex justify-between items-center shadow-inner">
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase font-bold">
                        <AlertCircle size={14} className="text-blue-500" />
                        <span>{t('enterMeters.operator')} <span className="text-blue-700 italic">{currentUser}</span></span>
                    </div>
                    
                    <div className="flex gap-3">
                        <button 
                            onClick={onClose} 
                            className="px-6 py-2 border border-gray-300 text-gray-600 font-bold text-[11px] hover:bg-white hover:shadow-sm transition-all uppercase rounded-sm"
                        >
                            {t('enterMeters.btnCancel')}
                        </button>
                        <button 
                            onClick={handleSave} 
                            className="px-10 py-2 bg-[#005495] text-white font-bold text-[11px] shadow-lg hover:bg-[#1a3353] hover:-translate-y-0.5 transition-all uppercase flex items-center gap-2 rounded-sm"
                        >
                            <Save size={16} /> {t('enterMeters.btnUpdate')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnterMeterReadings;